/**
 * web/src/main.tsx
 * React 19 root mount (Phase 5). Provider tree:
 *   StrictMode > ErrorBoundary > QueryClientProvider > BrowserRouter
 *     > WebSocketProvider > TooltipProvider > App + Toaster
 *
 * Provider order rationale (CONTEXT.md Pitfall 11 + 05-PATTERNS lines 169-198):
 *  - QueryClientProvider must be ABOVE WebSocketProvider — handleBroadcast
 *    calls qc.invalidateQueries / qc.setQueryData on the client instance
 *    consumed via useQueryClient inside the WS provider.
 *  - BrowserRouter sits between them so useNavigate works inside the
 *    WS-driven reconnect path; the queryClient global 401 handler
 *    (web/src/lib/queryClient.ts) drives the /login redirect from outside
 *    React, but pages-level navigations still need router context.
 *  - ErrorBoundary is outer-most so it catches render errors anywhere
 *    inside the tree (React 19 still scopes componentDidCatch to a class
 *    component — no hook equivalent).
 *  - TooltipProvider wraps App so per-tooltip useTooltip is cheap and a
 *    single shared delayDuration applies across the dashboard.
 *
 * Also kicks off `gcDrafts()` once at startup to remove any localStorage
 * gate drafts older than 30 days (CONTEXT.md D-4.3). The call is a
 * fire-and-forget side effect; it tolerates missing localStorage.
 */
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { TooltipProvider } from './components/ui/tooltip';
import App from './App';
import { queryClient } from './lib/queryClient';
import { WebSocketProvider } from './lib/ws/WebSocketProvider';
import { ErrorBoundary } from './components/layout/ErrorBoundary';
import { Toaster } from './components/ui/sonner';
import { gcDrafts } from './lib/forms/draft';
import './index.css';

// Lazy GC of stale gate drafts (CONTEXT.md D-4.3 — 30-day TTL).
gcDrafts();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <WebSocketProvider>
            <TooltipProvider delayDuration={250}>
              <App />
              <Toaster />
            </TooltipProvider>
          </WebSocketProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
